import {readImportFile} from "@/helpers/import/readImportFile";
import {cellKey, CLEAR, EDIT, ERROR, isCatalogFile, parseImportSheets, rowKey, SKIP} from "@/helpers/import/parseImportSheets";
import {columnExamples, guessColumnMapping, MAPPABLE_COLUMNS, mappedProductSheet, REQUIRED_FIELDS} from "@/helpers/import/mapImportColumns";
import {areOptionListsEnabled} from "@/helpers/featureFlags";
import {IMPORT_STEPS} from "@/helpers/import/importSteps";
import {buildImportReferences, USE_PREFIX} from "@/helpers/import/buildImportReferences";
import {groupPath} from "@/helpers/catalogWorkbookData";
import {buildImportGroups, CREATE_GROUP} from "@/helpers/import/resolveImportGroups";
import {buildImportOptionLists} from "@/helpers/import/resolveImportOptionLists";
import {buildImportPayload} from "@/helpers/import/buildImportPayload";
import {normalizeName} from "@/helpers/import/matchNames";
import {importService} from "@/services/ImportService";
import {toast} from "vue3-toastify";
import {i18n, SUPPORTED_LOCALES} from "@/lang";

const emptyResult = () => ({
    fileName: '',
    format: '',
    sheets: {},
    issues: [],
});

function fieldLabels() {
    return Object.fromEntries(MAPPABLE_COLUMNS.map(column => [
        column.key,
        SUPPORTED_LOCALES.map(locale => i18n.global.t(`importPage.columns.fields.${column.key}`, locale)),
    ]));
}

const state = {
    importStep: 0,
    importResult: emptyResult(),
    rawImportSheets: [],
    importFixes: {},
    skippedImportRows: {},
    editingImportCells: null,
    isExpressImportOpen: false,
    columnSources: [],
    columnSheetIndex: 0,
    columnMapping: {},
    referenceChoices: {},
    groupEdits: {},
    productSelection: {},
    importCatalogRequest: {
        loading: false,
    },
    readImportFileRequest: {
        loading: false,
    },
};

const getters = {
    importColumns(state) {
        const source = state.columnSources[state.columnSheetIndex];
        if (!source) return [];
        return source.header
            .map((name, index) => ({index, name, examples: columnExamples(source.rows, index), field: state.columnMapping[index] ?? null}))
            .filter(column => column.name || column.examples.length);
    },
    importCorrections(state) {
        const fixes = Object.entries(state.importFixes).map(([key, value]) => {
            const [sheet, row, column] = key.split('|');
            return {key, kind: value === '' ? CLEAR : EDIT, sheet, row: Number(row), column, value};
        });
        const skips = Object.keys(state.skippedImportRows).map(key => {
            const [sheet, row] = key.split('|');
            return {key, kind: SKIP, sheet, row: Number(row)};
        });
        return [...fixes, ...skips].sort((a, b) => a.sheet.localeCompare(b.sheet) || a.row - b.row);
    },
    missingImportFields(state) {
        const mapped = new Set(Object.values(state.columnMapping));
        return REQUIRED_FIELDS.filter(key => !mapped.has(key));
    },
    importOrgGroups(state, getters, rootState) {
        const groups = rootState.productGroup?.productGroups || [];
        const groupsById = new Map(groups.map(group => [group.productGroupId, group]));
        return {groups, paths: groups.map(group => groupPath(group.productGroupId, groupsById, group.name))};
    },
    importReferences(state, getters) {
        return buildImportReferences(state.importResult.sheets, getters.importOrgGroups.paths);
    },
    referenceChoice(state) {
        return reference => state.referenceChoices[reference.key] ?? reference.defaultChoice;
    },
    importReferenceChoices(state, getters) {
        return new Map(getters.importReferences.unresolved.map(reference => [reference.key, getters.referenceChoice(reference)]));
    },
    importGroupsResult(state, getters) {
        return buildImportGroups({
            sheets: state.importResult.sheets,
            orgGroups: getters.importOrgGroups.groups,
            orgPaths: getters.importOrgGroups.paths,
            choices: getters.importReferenceChoices,
        });
    },
    importGroups(state, getters) {
        return getters.importGroupsResult.groups.map(group => ({...group, values: {...group.defaults, ...(state.groupEdits[group.key] || {})}}));
    },
    importGroupsReady(state, getters) {
        const isSet = value => value !== null && value !== undefined;
        return getters.importGroups
            .filter(group => group.status === CREATE_GROUP)
            .every(group => isSet(group.values.vatIn) && isSet(group.values.vatOut));
    },
    importOptionListsResult(state, getters) {
        return buildImportOptionLists(state.importResult.sheets, getters.importReferenceChoices);
    },
    importOptionLists(state, getters) {
        return getters.importOptionListsResult.lists.map(list => ({...list, values: list.settings, productCount: list.productRows.size}));
    },
    importOrgProductKeys(state, getters, rootState) {
        const {groups, paths} = getters.importOrgGroups;
        const pathById = new Map(groups.map((group, index) => [group.productGroupId, paths[index]]));
        return new Set((rootState.product?.products || []).map(product => {
            const groupId = product.productGroupId ?? product.productGroup?.productGroupId;
            return `${normalizeName(pathById.get(groupId))}|${normalizeName(product.name)}`;
        }));
    },
    importSummaryProducts(state, getters) {
        const {resolve} = getters.importGroupsResult;
        const groupsByKey = new Map(getters.importGroups.map(group => [group.key, group]));
        return (state.importResult.sheets.Products?.rows || [])
            .filter(row => !row.excluded)
            .map(row => {
                const values = row.values;
                const groupPath = resolve(values.group);
                const group = groupsByKey.get(normalizeName(groupPath));
                const vatIn = values.vatIn ?? group?.values.vatIn ?? null;
                const existing = getters.importOrgProductKeys.has(`${normalizeName(groupPath)}|${normalizeName(values.name)}`);
                return {
                    rowNumber: row.rowNumber,
                    name: values.name,
                    groupPath,
                    price: values.priceVatInclIn,
                    vatIn,
                    vatOut: values.vatOut ?? values.vatIn ?? group?.values.vatOut ?? vatIn,
                    existing,
                    selected: state.productSelection[row.rowNumber] ?? !existing,
                };
            });
    },
    importSelectedRows(state, getters) {
        return new Set(getters.importSummaryProducts.filter(product => product.selected).map(product => product.rowNumber));
    },
    skippedImportSteps(state, getters) {
        const sheets = state.importResult.sheets;
        const hasLists = ['OptionLists', 'OptionListItems'].some(name => (sheets[name]?.rows || []).some(row => !row.excluded))
            || (sheets.Products?.rows || []).some(row => !row.excluded && row.values.optionLists?.length);
        return [
            ...(state.columnSources.length ? [] : ['columns']),
            ...(getters.importReferences.unresolved.length ? [] : ['references']),
            ...(hasLists ? [] : ['optionLists']),
        ];
    },
    importCounts(state) {
        const validRows = name => (state.importResult.sheets[name]?.rows || []).filter(row => !row.excluded).length;
        return {
            products: validRows('Products'),
            groups: validRows('Groups'),
            optionLists: validRows('OptionLists'),
        };
    },
    importSheetSummaries(state) {
        return Object.entries(state.importResult.sheets).map(([name, sheet]) => {
            const warnedRows = new Set(state.importResult.issues
                .filter(issue => issue.sheet === name && issue.row && issue.level !== ERROR)
                .map(issue => issue.row));
            const excluded = sheet.unusable ? sheet.total : sheet.rows.filter(row => row.excluded).length;
            return {
                name,
                total: sheet.total,
                imported: sheet.total - excluded,
                excluded,
                warned: warnedRows.size,
            };
        });
    },
    importPayload(state, getters) {
        return ({blackbox, locale}) => buildImportPayload({
            sheets: state.importResult.sheets,
            groups: getters.importGroups,
            resolveGroup: getters.importGroupsResult.resolve,
            lists: getters.importOptionLists,
            resolveList: getters.importOptionListsResult.resolveList,
            selectedRows: getters.importSelectedRows,
            blackbox,
            locale,
        });
    },
    expressImportBlocker(state, getters) {
        if (state.columnSources.length && getters.missingImportFields.length) return 'columns';
        if (!getters.canContinueImport) return 'blocked';
        if (!getters.importGroupsReady) return 'groupVat';
        if (!getters.importSelectedRows.size) return 'noProducts';
        return null;
    },
    expressImportSummary(state, getters) {
        const payload = getters.importPayload({blackbox: false, locale: 'fr'});
        const productRows = state.importResult.sheets.Products?.rows || [];
        return {
            products: payload.products.length,
            groupsCreated: payload.groups.filter(group => !group.productGroupId).length,
            groupsReused: payload.groups.filter(group => group.productGroupId).length,
            optionLists: payload.optionLists.length,
            excluded: productRows.filter(row => row.excluded).length,
            existing: getters.importSummaryProducts.filter(product => product.existing && !product.selected).length,
            matched: getters.importReferences.unresolved.filter(reference => getters.referenceChoice(reference).startsWith(USE_PREFIX)).length,
            renamed: getters.importCorrections.filter(correction => correction.kind === EDIT && correction.column === 'name').length,
        };
    },
    canContinueImport(state) {
        return state.importResult.issues.every(issue => issue.level !== ERROR || issue.row);
    },
};

const mutations = {
    setReadImportFileRequest(state, {loading, message}) {
        state.readImportFileRequest = {loading, message};
    },
    setImportResult(state, result) {
        state.importResult = {...emptyResult(), ...result};
    },
    setImportStep(state, step) {
        state.importStep = step;
    },
    setColumnSources(state, sources) {
        state.columnSources = sources;
    },
    setColumnSheetIndex(state, index) {
        state.columnSheetIndex = index;
    },
    setColumnMapping(state, mapping) {
        state.columnMapping = mapping;
    },
    setColumnField(state, {index, field}) {
        const mapping = Object.fromEntries(Object.entries(state.columnMapping).filter(([key, value]) => Number(key) !== index && value !== field));
        state.columnMapping = field ? {...mapping, [index]: field} : mapping;
    },
    resetReferenceChoices(state) {
        state.referenceChoices = {};
        state.groupEdits = {};
        state.productSelection = {};
    },
    setReferenceChoice(state, {key, value}) {
        state.referenceChoices = {...state.referenceChoices, [key]: value};
    },
    setProductsSelected(state, {rows, value}) {
        const selection = {...state.productSelection};
        rows.forEach(rowNumber => {
            selection[rowNumber] = value;
        });
        state.productSelection = selection;
    },
    setImportCatalogRequest(state, {loading, message}) {
        state.importCatalogRequest = {loading, message};
    },
    setGroupsVat(state, {keys, field, value}) {
        const groupEdits = {...state.groupEdits};
        keys.forEach(key => {
            groupEdits[key] = {...(groupEdits[key] || {}), [field]: value};
        });
        state.groupEdits = groupEdits;
    },
    setGroupEdit(state, {key, field, value}) {
        state.groupEdits = {...state.groupEdits, [key]: {...(state.groupEdits[key] || {}), [field]: value}};
    },
    setExpressImportOpen(state, open) {
        state.isExpressImportOpen = open;
    },
    setEditingImportCells(state, cells) {
        state.editingImportCells = cells;
    },
    setRawImportSheets(state, sheets) {
        state.rawImportSheets = sheets;
    },
    setImportFix(state, {key, value}) {
        const fixes = {...state.importFixes};
        if (value === undefined) delete fixes[key];
        else fixes[key] = value;
        state.importFixes = fixes;
    },
    setSkippedImportRow(state, {key, skipped}) {
        const rows = {...state.skippedImportRows};
        if (skipped) rows[key] = true;
        else delete rows[key];
        state.skippedImportRows = rows;
    },
    resetImportFixes(state) {
        state.importFixes = {};
        state.skippedImportRows = {};
    },
    resetImport(state) {
        state.importResult = emptyResult();
        state.importStep = 0;
        state.rawImportSheets = [];
        state.importFixes = {};
        state.skippedImportRows = {};
        state.editingImportCells = null;
        state.isExpressImportOpen = false;
        state.columnSources = [];
        state.columnSheetIndex = 0;
        state.columnMapping = {};
        state.referenceChoices = {};
        state.groupEdits = {};
        state.productSelection = {};
    },
};

const actions = {
    openExpressImport({state, commit, dispatch}) {
        const suggestions = state.importResult.issues
            .filter(issue => issue.params?.suggestion)
            .map(issue => ({sheet: issue.sheet, row: issue.row, column: issue.column, value: issue.params.suggestion}));
        if (suggestions.length) dispatch('fixImportCells', suggestions);
        commit('setExpressImportOpen', true);
    },
    async submitImport({state, getters, commit}, {blackbox, locale}) {
        commit('setImportCatalogRequest', {loading: true});
        try {
            await importService.importCatalog(getters.importPayload({blackbox, locale}));
            commit('product/invalidateProducts', null, {root: true});
            commit('productGroup/invalidateProductGroups', null, {root: true});
            commit('setImportCatalogRequest', {loading: false});
            toast.success(i18n.global.t('importPage.summary.success'));
            return true;
        } catch (error) {
            commit('setImportCatalogRequest', {loading: false, message: error});
            toast.error(typeof error === 'string' && error ? error : i18n.global.t('toast.createError'));
            return false;
        }
    },
    parseImport({state, commit}) {
        const mapped = state.columnSources.length > 0;
        const sheets = mapped
            ? [mappedProductSheet(state.columnSources[state.columnSheetIndex], state.columnMapping)]
            : state.rawImportSheets;
        const parsed = parseImportSheets(sheets, {
            withOptionLists: !mapped && areOptionListsEnabled(),
            fixes: state.importFixes,
            skippedRows: state.skippedImportRows,
        });
        commit('setImportResult', {fileName: state.importResult.fileName, format: state.importResult.format, ...parsed});
    },
    applyColumnMapping({commit, dispatch}) {
        commit('resetImportFixes');
        dispatch('parseImport');
        commit('resetReferenceChoices');
    },
    fixImportCells({commit, dispatch}, cells) {
        cells.forEach(({sheet, row, column, value}) => commit('setImportFix', {key: cellKey(sheet, row, column), value}));
        dispatch('parseImport');
    },
    skipImportRows({commit, dispatch}, rows) {
        rows.forEach(({sheet, row}) => commit('setSkippedImportRow', {key: rowKey(sheet, row), skipped: true}));
        dispatch('parseImport');
    },
    undoImportCorrection({commit, dispatch}, correction) {
        if (correction.kind === SKIP) commit('setSkippedImportRow', {key: correction.key, skipped: false});
        else commit('setImportFix', {key: correction.key, value: undefined});
        dispatch('parseImport');
    },
    selectColumnSheet({state, commit, dispatch}, index) {
        commit('setColumnSheetIndex', index);
        commit('setColumnMapping', guessColumnMapping(state.columnSources[index].header, fieldLabels()));
        dispatch('applyColumnMapping');
    },
    updateColumnField({commit, dispatch}, {index, field}) {
        commit('setColumnField', {index, field});
        dispatch('applyColumnMapping');
    },
    async readImportFile({commit, dispatch}, file) {
        commit('setReadImportFileRequest', {loading: true});
        try {
            const {format, sheets} = await readImportFile(file);
            const sources = sheets.filter(sheet => sheet.header.length && sheet.rows.length);
            commit('resetReferenceChoices');
            commit('resetImportFixes');
            commit('setImportResult', {fileName: file.name, format});
            if (isCatalogFile(sheets) || !sources.length) {
                commit('setColumnSources', []);
                commit('setRawImportSheets', sheets);
                dispatch('parseImport');
                commit('setImportStep', IMPORT_STEPS.indexOf('check'));
            } else {
                commit('setColumnSources', sources);
                commit('setRawImportSheets', []);
                dispatch('selectColumnSheet', 0);
                commit('setImportStep', IMPORT_STEPS.indexOf('columns'));
            }
            commit('setReadImportFileRequest', {loading: false});
        } catch (error) {
            const code = error?.code || 'readError';
            commit('setColumnSources', []);
            commit('setRawImportSheets', []);
            commit('setImportResult', {fileName: file.name, issues: [{code, level: ERROR, params: {}}]});
            commit('setImportStep', IMPORT_STEPS.indexOf('check'));
            commit('setReadImportFileRequest', {loading: false, message: code});
        }
    },
};

export const catalogImport = {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
